/**
 * Hero premium del perfil público del centro (`/reservar/{slug}`).
 *
 * Se renderiza ÚNICAMENTE en el step inicial (STEP_SERVICE) reemplazando el
 * antiguo hero + CenterInfoCard + galería + reviews. El flujo de booking
 * (steps de servicio/addons/slot/data) NO se toca.
 *
 * Sustituye en el JSX antiguo:
 *   - hero superpuesto con foto + título + descripción
 *   - CenterInfoCard (dirección, teléfono, redes)
 *   - bloque galería de trabajos
 *   - bloque reseñas
 *   - + 4 tabs nuevos: Trabajos · Reseñas · Vehículos · Promociones
 *
 * Diseño inspirado en el mockup `reservar.html` pero usando datos REALES
 * que ya devuelve `/api/public/org/{slug}`.
 */
import { useState } from "react";
import {
  MapPin, Phone, NavigationArrow, InstagramLogo, FacebookLogo,
  Clock, Star, ShareNetwork, ShieldCheck, ImageSquare, ChatCircleText,
  Sparkle, CaretRight, ArrowRight,
} from "@phosphor-icons/react";
import { StarRating } from "../../pages/components/StarRating";
import GalleryCarousel from "../../pages/components/GalleryCarousel";
import ReviewsSection from "../../pages/components/ReviewsSection";
import SocialActions from "./SocialActions";
import { fmtEur } from "../../lib/api";

const DAY_LABELS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

function buildHoursList(perDay) {
  // perDay: { "0":[{start,end}, ...], ... } | { mon:[...], ... }
  const KEYS = [["0","mon","lun"], ["1","tue","mar"], ["2","wed","mie"], ["3","thu","jue"], ["4","fri","vie"], ["5","sat","sab"], ["6","sun","dom"]];
  const out = [];
  for (let i = 0; i < 7; i++) {
    let tramos = null;
    for (const k of KEYS[i]) { if (perDay[k]?.length) { tramos = perDay[k]; break; } }
    out.push({
      label: DAY_LABELS[i],
      tramos: tramos ? tramos.map((t) => `${t.start || t.from || "—"}–${t.end || t.to || "—"}`).join(" · ") : null,
    });
  }
  return out;
}

export default function CentroProfileHero({ org, slug, brandColor, onStartBooking }) {
  const [tab, setTab] = useState("trabajos");

  const hours = buildHoursList(org.opening_hours_per_day || {});
  const todayIdx = (new Date().getDay() + 6) % 7;
  const todayHours = hours[todayIdx];
  const isOpenToday = !!todayHours?.tramos;
  // Sprint P.1.8 · Bloque B (CP10) · distinguir "sin horario configurado" vs "cerrado hoy":
  //   - Si NINGÚN día tiene tramos → el centro no gestiona horario → mostrar "Horario no disponible"
  //   - Si tiene tramos otros días pero HOY no → mostrar "Cerrado hoy" (correcto)
  //   - Si tiene tramos hoy → mostrar "Abierto · HH:MM–HH:MM"
  const hasAnyHours = hours.some((h) => !!h?.tramos);
  const hasHero = !!org.hero_image_url;
  const heroBg = hasHero
    ? `linear-gradient(180deg, rgba(15,17,23,0.15) 0%, rgba(15,17,23,0.85) 100%), url("${org.hero_image_url}")`
    : `linear-gradient(135deg, ${brandColor}30 0%, #0F1117 90%)`;

  const mapsUrl = org.google_maps_url ||
    (org.address || org.city
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([org.name, org.address, org.city].filter(Boolean).join(", "))}`
      : "");

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: org.name, text: org.description || "", url }); }
      catch { /* user cancelled */ }
    } else {
      try { await navigator.clipboard.writeText(url); }
      catch { /* clipboard blocked */ }
    }
  };

  // Promociones derivadas: addons con descuento o servicios marcados como "destacado"
  const promos = (org.catalog || []).filter((s) => s.is_featured || s.discount_pct);

  return (
    <div className="bg-[#F7F6F2] dark:bg-[#0F1117] -mx-4 sm:-mx-6 -mt-px transition-colors" data-testid="centro-profile-hero">
      {/* Cover full-width premium con gradiente y vignette */}
      <div
        className="relative h-[260px] sm:h-[380px] lg:h-[440px] bg-cover bg-center"
        style={{ backgroundImage: heroBg, backgroundColor: "#0F1117" }}
        data-testid="centro-cover"
      >
        {/* Vignette inferior para contraste con la card flotante */}
        <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-[#0F1117] via-[#0F1117]/60 to-transparent dark:from-[#0F1117]" />
        {/* Halo dorado decorativo */}
        <div className="pointer-events-none absolute top-0 right-0 w-1/2 h-full opacity-30 dark:opacity-40"
          style={{ background: "radial-gradient(closest-side at 80% 20%, rgba(212,175,55,0.4), transparent 60%)" }} />

        <button
          onClick={share}
          className="absolute top-4 right-4 w-10 h-10 rounded-full backdrop-blur grid place-items-center shadow-md hover:scale-105 transition active:scale-95
            bg-white/95 dark:bg-[#1A1D26]/90 dark:ring-1 dark:ring-white/10"
          aria-label="Compartir"
          data-testid="centro-share"
        >
          <ShareNetwork size={16} weight="bold" className="text-[#1A1A1A] dark:text-[#F5C76A]" />
        </button>
      </div>

      {/* Card flotante con info */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 -mt-20 sm:-mt-28 relative z-10">
        <div className="rounded-2xl shadow-xl border p-5 sm:p-6 transition-colors
          bg-white border-gray-100
          dark:bg-[#1A1D26] dark:border-white/10 dark:shadow-[0_30px_80px_-30px_rgba(0,0,0,0.7)]">

          {/* Layout izquierdo asimétrico — avatar circular con anillo dorado */}
          <div className="flex items-start gap-4 sm:gap-5">
            <div className="relative shrink-0">
              {org.logo_url ? (
                <img
                  src={org.logo_url}
                  alt={org.name}
                  className="w-20 h-20 sm:w-24 sm:h-24 rounded-full object-cover ring-4 ring-[#D4AF37] dark:ring-[#F5C76A] shadow-lg"
                  data-testid="centro-profile-avatar"
                />
              ) : (
                <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full grid place-items-center font-display font-black text-3xl text-[var(--ag-text)] ring-4 ring-[#D4AF37] dark:ring-[#F5C76A] shadow-lg"
                  style={{ backgroundColor: brandColor }}
                  data-testid="centro-profile-avatar"
                >
                  {org.name?.charAt(0).toUpperCase() || "?"}
                </div>
              )}
              {/* Sprint P.0 · fix P0-B: usar `verification_status` (schema real) en lugar
                  del campo inexistente `is_verified`. Sólo se muestra el badge azul
                  cuando la org está verificada por fuente oficial (RASIC), verificada
                  manualmente o ya reclamada por su titular · NO inventar datos. */}
              {["rasic_verified", "verified", "claimed"].includes(org.verification_status) && (
                <span
                  className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-[#0077B6] grid place-items-center ring-2 ring-white dark:ring-[#1A1D26] shadow-md"
                  title={org.verification_status === "rasic_verified"
                    ? "Datos verificados por RASIC (registro oficial)"
                    : (org.verification_status === "claimed" ? "Perfil gestionado por su titular" : "Centro verificado")}
                  data-testid="centro-verified-badge"
                >
                  <ShieldCheck size={14} weight="fill" className="text-[var(--ag-text)]" />
                </span>
              )}
            </div>

            <div className="flex-1 min-w-0">
              <h1
                className="font-display font-black text-xl sm:text-2xl tracking-tighter leading-tight text-[#1A1A1A] dark:text-white"
                data-testid="centro-profile-name"
              >
                {org.name}
              </h1>
              {(org.sectors || []).length > 0 && (
                <div className="text-xs sm:text-sm font-medium mt-1 text-gray-500 dark:text-gray-400" data-testid="centro-profile-sector">
                  {(org.sectors || []).slice(0, 2).map(s => typeof s === 'string' ? s : (s.name || s.label || '')).filter(Boolean).join(" · ")}
                </div>
              )}

              {/* Rating prominente */}
              {(org.rating_count || 0) > 0 ? (
                <div className="flex items-center gap-2 mt-2" data-testid="centro-rating">
                  <StarRating value={org.rating_avg || 0} size={14} />
                  <span className="font-display font-bold text-base tabular-nums text-[#1A1A1A] dark:text-white">
                    {Number(org.rating_avg || 0).toFixed(1)}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    ({org.rating_count} {org.rating_count === 1 ? "reseña" : "reseñas"})
                  </span>
                </div>
              ) : (
                <div className="text-xs mt-2 text-[var(--ag-text-muted)] dark:text-gray-500" data-testid="centro-rating-empty">
                  Aún sin reseñas — ¡sé el primero!
                </div>
              )}

              {/* Status pill */}
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                  isOpenToday
                    ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300"
                    : hasAnyHours
                      ? "bg-gray-100 text-gray-500 dark:bg-white/5 dark:text-gray-400"
                      : "bg-white/5 text-[var(--ag-text-dim)] border border-[var(--ag-border)]"
                }`} data-testid="centro-status-pill">
                  <span className={`w-1.5 h-1.5 rounded-full ${isOpenToday ? "bg-emerald-500" : hasAnyHours ? "bg-gray-400" : "bg-gray-500"}`} />
                  {isOpenToday
                    ? `Abierto · ${todayHours.tramos}`
                    : hasAnyHours
                      ? "Cerrado hoy"
                      : "Horario no disponible"}
                </span>
              </div>

              {/* F3 · Acciones sociales: Seguir / Mensaje */}
              <div className="mt-3">
                <SocialActions slug={slug} orgName={org.name} />
              </div>
            </div>
          </div>

          {/* Dirección bajo el cabecero */}
          {org.address && (
            <div className="text-xs sm:text-sm mt-4 flex items-start gap-1.5 text-gray-600 dark:text-gray-300">
              <MapPin size={14} weight="fill" className="mt-0.5 shrink-0 text-[var(--ag-text-muted)] dark:text-[#F5C76A]" />
              <span>{org.address}{(org.city || org.locality) && ` · ${[org.locality, org.city].filter(Boolean).join(", ")}`}</span>
            </div>
          )}

          {org.description && (
            <p className="text-sm leading-relaxed mt-4 text-gray-700 dark:text-gray-300">{org.description}</p>
          )}

          {/* F2 · Pills de servicios destacados (3-5 más representativos) */}
          {(org.catalog || []).length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4" data-testid="centro-service-pills">
              {(org.catalog || []).slice(0, 5).map((c) => (
                <span
                  key={c.id}
                  className="inline-flex items-center gap-1 text-[11px] font-semibold px-3 py-1.5 rounded-full transition
                    bg-[#FAF0D7] text-[#7C5E0B] hover:bg-[#F5E1A4]
                    dark:bg-[#2A2417] dark:text-[#F5C76A] dark:hover:bg-[#3A2E0E]"
                  data-testid={`centro-service-pill-${c.id}`}
                >
                  <Sparkle size={10} weight="fill" /> {c.name}
                </span>
              ))}
              {(org.catalog || []).length > 5 && (
                <span className="inline-flex items-center text-[11px] font-semibold px-3 py-1.5 rounded-full
                  bg-gray-100 text-gray-600 dark:bg-white/5 dark:text-gray-300">
                  +{(org.catalog || []).length - 5} más
                </span>
              )}
            </div>
          )}

          {/* CTA principal · Sprint P.1.5 · Bloque I (CP4): en Mobile <640px
              se oculta este botón para no competir con el sticky bottom
              (más accesible al pulgar). En sm+ se muestra normalmente. */}
          <button
            onClick={onStartBooking}
            data-testid="centro-cta-book"
            className="hidden sm:flex w-full h-12 mt-5 rounded-xl font-bold text-sm tracking-wide items-center justify-center gap-2 transition active:scale-[0.98] shadow-lg shadow-amber-900/20
              bg-gradient-to-r from-[#D4AF37] to-[#B8860B] hover:from-[#B8860B] hover:to-[#996F0A]
              dark:from-[#F5C76A] dark:to-[#D4AF37] dark:hover:from-[#D4AF37] dark:hover:to-[#B8860B]
              text-[#1A1A1A]"
          >
            Reservar ahora <ArrowRight size={16} weight="bold" />
          </button>

          {/* Acciones secundarias */}
          <div className="grid grid-cols-3 gap-2 mt-3">
            {org.phone && (
              <a href={`tel:${org.phone}`} className="h-10 rounded-lg flex items-center justify-center gap-1.5 text-xs font-semibold transition
                bg-gray-50 hover:bg-gray-100 text-[#1A1A1A]
                dark:bg-white/5 dark:hover:bg-white/10 dark:text-white" data-testid="centro-call">
                <Phone size={13} weight="fill" /> Llamar
              </a>
            )}
            {mapsUrl && (
              <a href={mapsUrl} target="_blank" rel="noreferrer" className="h-10 rounded-lg flex items-center justify-center gap-1.5 text-xs font-semibold transition
                bg-gray-50 hover:bg-gray-100 text-[#1A1A1A]
                dark:bg-white/5 dark:hover:bg-white/10 dark:text-white" data-testid="centro-maps">
                <NavigationArrow size={13} weight="fill" /> Cómo llegar
              </a>
            )}
            {/* Sprint P.0 · fix P0-C: WhatsApp SÓLO si `org.whatsapp` existe explícitamente.
                Nunca hacer fallback a `org.phone` (fijo) porque generaría un enlace muerto
                (`wa.me/<tel-fijo>` no responde). Regla founder: no inventar información. */}
            {org.whatsapp && (
              <a
                href={`https://wa.me/${String(org.whatsapp).replace(/[^0-9+]/g, "")}`}
                target="_blank" rel="noreferrer"
                className="h-10 rounded-lg flex items-center justify-center gap-1.5 text-xs font-semibold transition
                  bg-emerald-50 hover:bg-emerald-100 text-emerald-700
                  dark:bg-emerald-500/10 dark:hover:bg-emerald-500/20 dark:text-emerald-300"
                data-testid="centro-whatsapp"
              >
                <ChatCircleText size={13} weight="fill" /> WhatsApp
              </a>
            )}
          </div>

          {/* Redes sociales */}
          {(org.instagram_url || org.facebook_url || org.website_url) && (
            <div className="flex items-center gap-2 mt-4 pt-4 border-t border-gray-100 dark:border-white/10">
              {org.instagram_url && (
                <a href={org.instagram_url} target="_blank" rel="noreferrer" className="w-8 h-8 rounded-full bg-gradient-to-br from-pink-500 to-orange-400 grid place-items-center text-[var(--ag-text)] hover:scale-110 transition" aria-label="Instagram">
                  <InstagramLogo size={14} weight="fill" />
                </a>
              )}
              {org.facebook_url && (
                <a href={org.facebook_url} target="_blank" rel="noreferrer" className="w-8 h-8 rounded-full bg-[#1877F2] grid place-items-center text-[var(--ag-text)] hover:scale-110 transition" aria-label="Facebook">
                  <FacebookLogo size={14} weight="fill" />
                </a>
              )}
              {org.website_url && (
                <a href={org.website_url} target="_blank" rel="noreferrer" className="text-xs ml-auto font-medium underline-offset-2 hover:underline
                  text-gray-600 hover:text-[#1A1A1A]
                  dark:text-gray-300 dark:hover:text-[#F5C76A]">
                  {org.website_url.replace(/^https?:\/\//, "").replace(/\/$/, "").slice(0, 30)}
                </a>
              )}
            </div>
          )}
        </div>

        {/* Tabs · Sprint P.0 · fix P0-D: eliminado tab "Vehículos" hardcoded que
            pintaba emojis y tarjetas fake iguales para todos los sectores.
            Cuando exista `org.vehicle_types` real se podrá reintroducir. */}
        <div className="flex items-center gap-1 mt-6 overflow-x-auto pb-1 no-scrollbar">
          {[
            { id: "trabajos", label: "Trabajos", icon: ImageSquare, count: (org.gallery || []).length },
            { id: "reviews", label: "Reseñas", icon: Star, count: org.rating_count || 0 },
            { id: "horarios", label: "Horario", icon: Clock },
            { id: "promos", label: "Promociones", icon: Sparkle, count: promos.length },
          ].map((t) => {
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`centro-tab-${t.id}`}
                className={`flex items-center gap-1.5 px-4 h-10 rounded-full text-xs font-semibold whitespace-nowrap transition ${
                  active
                    ? "text-[var(--ag-text)] shadow-md"
                    : "bg-white text-gray-600 hover:bg-gray-50 border border-gray-200 dark:bg-[#1A1D26] dark:text-gray-300 dark:hover:bg-white/5 dark:border-white/10"
                }`}
                style={active ? { backgroundColor: brandColor } : {}}
              >
                <t.icon size={12} weight={active ? "fill" : "regular"} />
                {t.label}
                {t.count > 0 && (
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
                    active ? "bg-white/20" : "bg-gray-100 dark:bg-white/10"
                  }`}>
                    {t.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Tab contents */}
        <div className="mt-4 mb-8">
          {tab === "trabajos" && (
            <div data-testid="centro-tab-trabajos-content">
              {(org.gallery || []).length > 0 ? (
                <div className="rounded-xl p-4 sm:p-5 border bg-white border-gray-100 dark:bg-[#1A1D26] dark:border-white/10">
                  <GalleryCarousel items={org.gallery} testId="centro-gallery" />
                </div>
              ) : (
                <EmptyState icon={ImageSquare} text="Este centro aún no ha publicado fotos de trabajos." />
              )}
            </div>
          )}

          {tab === "reviews" && (
            <div className="rounded-xl p-4 sm:p-5 border bg-white border-gray-100 dark:bg-[#1A1D26] dark:border-white/10" data-testid="centro-tab-reviews-content">
              <ReviewsSection slug={slug} orgName={org.name} />
            </div>
          )}

          {tab === "horarios" && (
            <div className="rounded-xl p-4 sm:p-5 border bg-white border-gray-100 dark:bg-[#1A1D26] dark:border-white/10" data-testid="centro-tab-horarios-content">
              <div className="flex items-center gap-2 mb-3">
                <Clock size={14} weight="duotone" className="text-[#B8860B] dark:text-[#F5C76A]" />
                <h3 className="font-display font-bold tracking-tight text-sm text-[#1A1A1A] dark:text-white">Horario semanal</h3>
              </div>
              <ul className="text-sm divide-y divide-gray-100 dark:divide-white/5">
                {hours.map((h, i) => (
                  <li key={i} className={`flex items-center justify-between py-2 ${
                    i === todayIdx ? "font-bold text-[#1A1A1A] dark:text-white" : "text-gray-700 dark:text-gray-300"
                  }`}>
                    <span className="flex items-center gap-2">
                      {h.label}
                      {i === todayIdx && (
                        <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full font-bold
                          bg-[#D4AF37] text-[#1A1A1A] dark:bg-[#F5C76A]">
                          Hoy
                        </span>
                      )}
                    </span>
                    <span className={h.tramos ? "" : "text-[var(--ag-text-muted)] dark:text-gray-500"}>{h.tramos || "Cerrado"}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {tab === "promos" && (
            <div data-testid="centro-tab-promos-content">
              {promos.length > 0 ? (
                <div className="grid sm:grid-cols-2 gap-3">
                  {promos.map((p) => (
                    <div key={p.id} className="rounded-xl p-4 border bg-white border-gray-100 dark:bg-[#1A1D26] dark:border-white/10">
                      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider font-bold mb-2 text-[#B8860B] dark:text-[#F5C76A]">
                        <Sparkle size={11} weight="fill" /> {p.is_featured ? "Destacado" : `-${p.discount_pct}%`}
                      </div>
                      <div className="font-display font-bold tracking-tight text-[#1A1A1A] dark:text-white">{p.name}</div>
                      <div className="text-xs mt-1 line-clamp-2 text-gray-500 dark:text-gray-400">{p.description || "Servicio premium"}</div>
                      <div className="text-sm font-bold mt-3 text-[#B8860B] dark:text-[#F5C76A]">{fmtEur(p.price)}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState icon={Sparkle} text="Sin promociones activas en este momento." />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function EmptyState({ icon: Icon, text }) {
  return (
    <div className="rounded-xl p-8 text-center border border-dashed
      bg-white border-gray-200
      dark:bg-[#1A1D26]/50 dark:border-white/10">
      <Icon size={24} weight="duotone" className="mx-auto mb-2 text-[var(--ag-text-dim)] dark:text-gray-600" />
      <p className="text-sm text-gray-500 dark:text-gray-400">{text}</p>
    </div>
  );
}
